/**
 * Redux selectors for multi-resolution data
 * 
 * These selectors provide easy access to resolution metadata and cached data
 */

import { RootState } from "../../app/store";
import { ScaleMetadata, TrackDataCacheEntry, getCacheKey } from "./resolutionHelpers";

/**
 * Get resolution metadata for a specific track type
 */
export const selectResolutionMetadata = (state: RootState, trackTypeId: number): ScaleMetadata | undefined => {
  return state.template.resolutionMetadata[trackTypeId];
};

/**
 * Get all resolution metadata
 */
export const selectAllScaleMetadata = (state: RootState) => {
  return state.template?.scaleMetadata;
};

/**
 * Get cached track data for specific parameters
 */
export const selectCachedTrackData = (
  state: RootState,
  trackTypeId: number,
  boreholeId: number | undefined,
  scale: number
): TrackDataCacheEntry | undefined => {
  const cacheKey = getCacheKey(trackTypeId, boreholeId, scale);
  return state.template?.trackDataCache[cacheKey];
};

/**
 * Get all cached data for a track type (all scales)
 */
export const selectAllCachedDataForTrackType = (
  state: RootState,
  trackTypeId: number,
  boreholeId?: number
): TrackDataCacheEntry[] => {
  const cache = state.template?.trackDataCache;
  const entries: TrackDataCacheEntry[] = [];

  Object.keys(cache).forEach(key => {
    const entry = cache[key];
    if (entry.trackTypeId === trackTypeId) {
      if (boreholeId === undefined || entry.boreholeId === boreholeId) {
        entries.push(entry);
      }
    }
  });

  return entries.sort((a, b) => a.scale - b.scale);
};

/**
 * Check if high resolution data is currently loading
 */
export const selectIsLoadingHighResolution = (state: RootState): boolean => {
  return state.template?.loadingHighResolution;
};

/**
 * Get the track data cache
 */
export const selectTrackDataCache = (state: RootState) => {
  return state.template?.trackDataCache;
};
